/**
 * The cue builders — ONE source of copy for text, captions and speech (SPEC_CAPTURE_UX_R2 §3.1).
 *
 * Every cue's `text` is a string the screen is ALREADY showing:
 *   · a step cue speaks `shotText(shot, locale)` — the server's prompt in the chosen language, verbatim, exactly
 *     what the instruction card prints (round 1 §1.3; FSD-18 D1). The captions ARE the instruction card; there is
 *     no second caption track to author and no second script to drift. The cue carries the language the words are
 *     actually in (`textLocale`), so English fallback text is never read by a Hindi voice (D6);
 *   · an error cue speaks the error card's own title and body (§3.3 "the same words as the visible card") — the
 *     words are passed in by the card, so a copy change moves both channels at once;
 *   · a notice cue speaks one section of the notice the customer is scrolling (§3.2), flagged `aidOnly`: an
 *     accessibility aid, never consent — the reviewed asset when there is one, otherwise the device voice
 *     (US-03482 G7, 2026-11-03).
 */

import type { ScriptShot } from '@/api/capture';
import { shotText } from '@/i18n/serverText';
import { type GuidanceAudioRef, audioRefFor } from './audioAsset';
import type { GuidanceCue } from './GuidancePlayer';
import type { NoticeSection } from './noticeSections';

/**
 * PROPOSED contract v0.1.13 §4: `script[].audio?: {<lang>: AudioRef}`. NO be sends it today (SPEC_AUDIO_GUIDANCE
 * §2: the publish job does not exist), so it is read off the shot defensively and never required — the seam, not
 * a dependency. The client's wire mirror (`api/capture.ts`) stays as the be actually sends it.
 */
type ShotWithAudio = ScriptShot & { readonly audio?: unknown };

export function stepCue(shot: ScriptShot, locale: string): GuidanceCue {
  const asset: GuidanceAudioRef | null = audioRefFor((shot as ShotWithAudio).audio, locale);
  const words = shotText(shot, locale);
  return { key: `STEP:${shot.shotId}`, text: words.text, locale, textLocale: words.locale, ...(asset ? { asset } : {}) };
}

/** An error card's spoken form. `key` names the card (`camera` / `location` / a gate code), never the text. */
export function errorCue(key: string, words: readonly string[], locale: string): GuidanceCue | null {
  const text = words.map((w) => (w ?? '').trim()).filter((w) => w !== '').join(' ');
  if (text === '') return null;
  return { key: `ERROR:${key}`, text, locale };
}

/**
 * A notice section's cue. F9 (US-03482, 2026-11-03): the notice is read in the voice of the language it is WRITTEN
 * in (`notice.locale`), not the deck language — an English notice on the Hindi deck is read by an English voice, and
 * never by a Hindi voice reading English. `noticeLocale` absent ⇒ the deck locale (the notice is in the deck language).
 */
export function noticeCue(section: NoticeSection, locale: string, version: string, noticeLocale?: string): GuidanceCue {
  const lang = noticeLocale || locale;
  return { key: `NOTICE:${version}:${section.index}`, text: section.text, locale: lang, textLocale: lang, aidOnly: true };
}

/**
 * The reviewed audio a notice may carry (PROPOSED contract v0.1.13 §4: `summary.notices[].audio?: {<lang>: AudioRef}`,
 * `aidOnly`). Read tolerantly, because the sibling spec allows both shapes and the be sends neither yet:
 *   · an ARRAY — one asset per section, so the read can be stopped and resumed section by section;
 *   · a single ref — the plane concatenated the whole notice into one clip with gaps (SPEC_AUDIO_GUIDANCE §2.1),
 *     so the sections are inside it and the client plays it as one cue rather than pretending to cut it.
 */
export function noticeAudioRefs(audio: unknown, locale: string): readonly GuidanceAudioRef[] {
  if (!audio || typeof audio !== 'object') return [];
  const map = audio as Record<string, unknown>;
  const lang = locale.toLowerCase();
  const base = lang.split('-')[0] ?? lang;
  for (const key of Object.keys(map)) {
    const k = key.toLowerCase();
    if (k !== lang && k !== base && k.split('-')[0] !== base) continue;
    const value = map[key];
    if (Array.isArray(value)) return value.filter((v): v is GuidanceAudioRef => !!v && typeof v === 'object');
    if (value && typeof value === 'object') return [value as GuidanceAudioRef];
  }
  return [];
}

export function noticeCues(
  sections: readonly NoticeSection[],
  locale: string,
  version: string,
  audio?: unknown,
  noticeLocale?: string,
): readonly GuidanceCue[] {
  const lang = noticeLocale || locale;
  const refs = noticeAudioRefs(audio, lang);
  if (refs.length === 1 && sections.length > 1) {
    return [
      {
        key: `NOTICE:${version}:all`,
        text: sections.map((s) => s.text).join(' '),
        locale: lang,
        textLocale: lang,
        aidOnly: true,
        asset: refs[0] as GuidanceAudioRef,
      },
    ];
  }
  return sections.map((s, i) => {
    const ref = refs[i];
    return { ...noticeCue(s, locale, version, lang), ...(ref ? { asset: ref } : {}) };
  });
}
